import React, { memo, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Trans, t } from '@lingui/macro';
import ReactFlow, {
  Controls,
  Background,
  MiniMap,
  useNodesState,
  useEdgesState,
} from 'reactflow';
import 'reactflow/dist/style.css';

import aslToFlow from './aslToFlow';
import sfnNodeTypes from './SfnNodeTypes';
import {
  CommandCard,
  CommandCardHeader,
  CommandCardTitle,
  StateBadge,
  CommandCardBody,
} from './style';
import { START_END_COLOR, typeColor } from './graphPalette';

// Stable default: a fresh literal here would change identity every render
// and re-fire the hooks that depend on it.
const NO_STATES = {};

const MINIMAP_STYLE = { height: 80, width: 120 };

function minimapNodeColor(node) {
  return node.type === 'startEnd' ? START_END_COLOR : typeColor(node.type);
}

function CommandsGraph({ name, startAt, states = NO_STATES }) {
  const { nodes: initialNodes, edges: initialEdges } = useMemo(
    () => aslToFlow({ startAt, states }),
    [startAt, states],
  );

  const [nodes, , onNodesChange] = useNodesState(initialNodes);
  const [edges, , onEdgesChange] = useEdgesState(initialEdges);

  const stateCount = Object.keys(states || {}).length;

  const onInit = useCallback((instance) => {
    setTimeout(() => instance.fitView({ padding: 0.2 }), 50);
  }, []);

  return (
    <CommandCard>
      <CommandCardHeader>
        <CommandCardTitle>{name || t`Step Function`}</CommandCardTitle>
        <StateBadge>
          {stateCount} <Trans>states</Trans>
        </StateBadge>
      </CommandCardHeader>
      <CommandCardBody>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          nodeTypes={sfnNodeTypes}
          onInit={onInit}
          fitView
          fitViewOptions={{ padding: 0.2 }}
          minZoom={0.3}
          maxZoom={1.5}
          attributionPosition="bottom-left"
          proOptions={{ hideAttribution: true }}
        >
          <Controls showInteractive={false} />
          <Background color="var(--if-graph-bg)" gap={16} size={1} />
          <MiniMap
            style={MINIMAP_STYLE}
            nodeColor={minimapNodeColor}
            zoomable
            pannable
          />
        </ReactFlow>
      </CommandCardBody>
    </CommandCard>
  );
}
CommandsGraph.propTypes = {
  name: PropTypes.string.isRequired,
  startAt: PropTypes.string.isRequired,
  states: PropTypes.objectOf(PropTypes.shape({ Type: PropTypes.string })),
};

// ReactFlow re-initialises its whole store on every mount, so an unrelated
// parent render must not reach this component. All three props come straight
// from the fetched step-function object, so they are reference-stable.
export default memo(CommandsGraph);
